import React, { useMemo } from 'react';
import styled from 'styled-components';
import type { AnsweringServicePayer } from '@/app-package';
import { AnsweringServiceType } from '@bb/domain/dist/types';
import AnsweringServiceCard from './AnsweringServiceCard';

export interface AvailableServicesSectionProps {
  /** Whether there is an already active Answering Service subscription for this user/brokerage */
  hasActiveAnsweringService: boolean;
  /** What type of Answering Service this user/brokerge has (or is eligible to sign up for) */
  answeringServiceType: AnsweringServiceType;
  /** Who pays for the Answering Service subscription */
  answeringServicePayer: AnsweringServicePayer;
  /** Callback when LCS or Appointment Desk Learn More is clicked */
  onAnsweringServiceClick?: () => void;
}

/**
 * The "Available Services" section of the Subscription & Payments page
 * displaying all new subscriptions that the user/brokerage is eligible to
 * self-sign up for in the web app.
 */
const AvailableServicesSection: React.FC<AvailableServicesSectionProps> = ({
  hasActiveAnsweringService = false,
  answeringServiceType = AnsweringServiceType.LiveConciergePlus,
  // If no explicit payer is passed in, we default to 'user'
  // to allow a user or brokerage to pay for it themself.
  answeringServicePayer = 'user',
  onAnsweringServiceClick,
}) => {
  // Later hasAnyAvailableServices will need to accomodate Supra Access card, too
  const hasAnyAvailableServices = useMemo(
    () => !hasActiveAnsweringService,
    [hasActiveAnsweringService],
  );

  // If the user has no new subscriptions available to sign up for, don't display anything
  if (!hasAnyAvailableServices) {
    return null;
  }

  return (
    <Container>
      <Title>Available Services</Title>
      <CardsContainer>
        {!hasActiveAnsweringService &&
          answeringServiceType !== AnsweringServiceType.Legacy && (
            <AnsweringServiceCard
              isSubscribed={false}
              answeringServiceType={answeringServiceType}
              answeringServicePayer={answeringServicePayer}
              onButtonClick={onAnsweringServiceClick}
            />
          )}
      </CardsContainer>
    </Container>
  );
};

export default AvailableServicesSection;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Title = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #2b2b2b;
  margin: 0;
`;

const CardsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;
